import { useCallback, useEffect, useState } from "react";
import { BadgePercent, Check, ExternalLink, FlaskConical, Loader2, Receipt } from "lucide-react";
import api, { errMsg, money, openCheckout } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

export default function Plans() {
  const { company, user, refresh } = useAuth();
  const [plans, setPlans] = useState([]);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busyKey, setBusyKey] = useState("");
  const [checkoutUrl, setCheckoutUrl] = useState("");
  const [portalUrl, setPortalUrl] = useState("");
  const [billing, setBilling] = useState(null);
  const [cycle, setCycle] = useState("weekly");
  const [intro, setIntro] = useState(null);
  const [proration, setProration] = useState(null);
  const [cancelOpen, setCancelOpen] = useState(false);
  const isOwner = user?.role === "owner";
  // Every way a plan can be bought, read off the first paid plan the API returned.
  const terms = plans.find((p) => p.cycles?.length)?.cycles || [];

  const loadBilling = useCallback(() => {
    if (!isOwner) return;
    api.get("/billing/overview").then(({ data }) => setBilling(data)).catch(() => {});
  }, [isOwner]);

  useEffect(() => {
    api.get("/plans").then(({ data }) => setPlans(data.plans)).catch((e) => setError(errMsg(e)));
    api.get("/payments/intro-offer").then(({ data }) => setIntro(data)).catch(() => {});
    loadBilling();
  }, [loadBilling]);

  const switchPlan = async (planKey) => {
    setError("");
    setNotice("");
    setBusyKey(`test-${planKey}`);
    try {
      await api.post("/billing/test-plan", { plan: planKey });
      await refresh();
      loadBilling();
      setNotice(`Switched to ${plans.find((p) => p.key === planKey)?.name || planKey} — no payment taken.`);
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusyKey("");
    }
  };

  const openPortal = async () => {
    setError("");
    setPortalUrl("");
    setBusyKey("portal");
    try {
      const { data } = await api.post("/billing/portal", { origin_url: window.location.origin });
      if (!openCheckout(data.portal_url, setPortalUrl)) setPortalUrl(data.portal_url);
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusyKey("");
    }
  };

  const previewSwitch = async (plan, lookupKey) => {
    setError("");
    setNotice("");
    setBusyKey(`preview-${plan.key}`);
    try {
      const { data } = await api.post("/billing/change-plan/preview", { lookup_key: lookupKey });
      setProration({ ...data, lookup_key: lookupKey, key: plan.key });
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusyKey("");
    }
  };

  const confirmSwitch = async () => {
    setError("");
    setBusyKey("switch");
    try {
      const { data } = await api.post("/billing/change-plan", { lookup_key: proration.lookup_key });
      setProration(null);
      await refresh();
      loadBilling();
      setNotice(`Switched to ${data.plan_name} — you were only charged for the days you use.`);
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusyKey("");
    }
  };

  const cancelSubscription = async () => {
    setError("");
    setNotice("");
    setBusyKey("cancel");
    try {
      const { data } = await api.post("/billing/cancel");
      setCancelOpen(false);
      loadBilling();
      setNotice(data.ends_at
        ? `Your plan stops on ${data.ends_at.slice(0, 10)}. Everything keeps working until then.`
        : "Your plan will stop at the end of the week you've paid for.");
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusyKey("");
    }
  };

  const resumeSubscription = async () => {
    setError("");
    setNotice("");
    setBusyKey("resume");
    try {
      await api.post("/billing/resume");
      loadBilling();
      setNotice("Your plan is back on — nothing was interrupted.");
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusyKey("");
    }
  };

  const upgrade = async (plan) => {
    setError("");
    setCheckoutUrl("");
    setBusyKey(plan.key);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key: plan.lookup_key,
        origin_url: window.location.origin,
      });
      if (!openCheckout(data.checkout_url, setCheckoutUrl)) {
        // Popup blocked inside the preview frame — hand the customer a link they can click.
        setCheckoutUrl(data.checkout_url);
        setBusyKey("");
      }
    } catch (e) {
      setError(errMsg(e));
      setBusyKey("");
    }
  };

  return (
    <div data-testid="plans-page" className="space-y-8">
      <div>
        <p className="font-mono text-xs uppercase tracking-[0.28em] text-gold">Plans & billing</p>
        <h1 className="mt-3 text-3xl font-bold tracking-tight text-slate-50">Choose your plan</h1>
        <p className="mt-2 text-sm text-slate-400">
          Current plan: <span className="text-gold">{company?.plan_name}</span>. Branding, custom domain and
          custom email unlock on Business / Branded.
        </p>
      </div>

      {error && <p data-testid="plans-error" className="text-sm text-red-400">{error}</p>}
      {notice && <p data-testid="plans-notice" className="text-sm text-emerald-400">{notice}</p>}

      {isOwner && billing?.test_mode && (
        <div data-testid="plan-switcher" className="rounded-xl border border-sky-500/30 bg-sky-500/5 p-5">
          <div className="flex items-center gap-2">
            <FlaskConical className="h-4 w-4 text-sky-300" />
            <p className="font-mono text-[11px] uppercase tracking-widest text-sky-300">Test mode · plan switcher</p>
          </div>
          <p className="mt-2 text-xs text-slate-400">
            Hop between plans instantly to try the features — no payment taken. This strip disappears
            automatically once live Stripe keys are in use.
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            {plans.map((p) => (
              <Button key={p.key} data-testid={`test-plan-${p.key}`} size="sm"
                disabled={company?.plan === p.key || busyKey === `test-${p.key}`}
                onClick={() => switchPlan(p.key)}
                className={company?.plan === p.key
                  ? "bg-ink-600 text-slate-400"
                  : "bg-sky-500/20 text-sky-100 hover:bg-sky-500/30"}>
                {busyKey === `test-${p.key}` ? <Loader2 className="h-4 w-4 animate-spin" /> : p.name}
              </Button>
            ))}
          </div>
        </div>
      )}

      {isOwner && billing && (
        <div data-testid="billing-panel" className="rounded-xl border border-ink-600 bg-ink-800 p-6">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-2">
                <Receipt className="h-4 w-4 text-gold" />
                <p className="font-mono text-[11px] uppercase tracking-widest text-gold">Billing</p>
              </div>
              <p className="mt-2 text-sm text-slate-300">
                On <span className="text-gold">{billing.plan_name}</span>
                {billing.plan_since ? ` since ${billing.plan_since.slice(0, 10)}` : ""}.
              </p>
              {billing.discount && (
                <p data-testid="billing-discount" className="mt-2 inline-flex items-center gap-2 rounded-full border border-emerald-500/40 bg-emerald-500/10 px-3 py-1 font-mono text-[11px] uppercase tracking-widest text-emerald-300">
                  <BadgePercent className="h-3.5 w-3.5" /> Discount applied · {billing.discount.label}
                </p>
              )}
              {billing.downgrade_reason && (
                <p data-testid="billing-downgrade" className="mt-1 text-xs text-amber-300">
                  Moved to Free: {billing.downgrade_reason}.
                </p>
              )}
            </div>
            <Button data-testid="billing-portal-btn" variant="outline" disabled={!billing.manageable || busyKey === "portal"}
              onClick={openPortal}
              className="border-ink-600 bg-transparent text-slate-300 hover:text-gold">
              {busyKey === "portal" ? <Loader2 className="h-4 w-4 animate-spin" />
                : <><ExternalLink className="mr-2 h-4 w-4" /> Manage subscription</>}
            </Button>
          </div>

          {!billing.manageable && (
            <p data-testid="billing-not-manageable" className="mt-3 rounded-lg border border-ink-600 bg-ink-900/60 p-3 text-xs text-slate-400">
              This plan wasn't purchased through checkout, so there's no Stripe subscription to manage yet.
              Buy a plan above and card changes, invoices and cancellation all turn on here.
            </p>
          )}

          {portalUrl && (
            <a data-testid="billing-portal-link" href={portalUrl} target="_blank" rel="noreferrer"
              className="mt-3 block text-sm text-gold underline">
              Tap here to open your billing portal in a new tab.
            </a>
          )}

          {billing.subscription?.trial_end && (
            <p data-testid="billing-trial" className="mt-3 font-mono text-[11px] text-emerald-300">
              Free until {billing.subscription.trial_end.slice(0, 10)} — no charge before then.
            </p>
          )}

          {billing.subscribed && (
            <div className="mt-5 border-t border-ink-700/60 pt-4">
              {billing.subscription?.cancel_at_period_end ? (
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <p data-testid="billing-cancelling" className="text-xs text-amber-300">
                    Cancelling — your plan runs until{" "}
                    {(billing.subscription.renews_at || "").slice(0, 10) || "the end of this week"}.
                  </p>
                  <Button data-testid="billing-resume-btn" size="sm" disabled={busyKey === "resume"}
                    onClick={resumeSubscription} className="bg-gold text-ink-900 hover:bg-gold-light">
                    {busyKey === "resume" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Keep my plan"}
                  </Button>
                </div>
              ) : cancelOpen ? (
                <div data-testid="billing-cancel-confirm" className="space-y-3">
                  <p className="text-xs text-slate-400">
                    You'll keep every feature until the end of the week you've already paid for, then
                    drop to Free (2 trips a week). Your trips and customers stay exactly where they are.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <Button data-testid="billing-cancel-confirm-btn" size="sm" disabled={busyKey === "cancel"}
                      onClick={cancelSubscription}
                      className="bg-red-500/90 text-white hover:bg-red-500">
                      {busyKey === "cancel" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Yes, cancel it"}
                    </Button>
                    <Button data-testid="billing-cancel-dismiss" size="sm" variant="ghost"
                      onClick={() => setCancelOpen(false)} className="text-slate-400">
                      Never mind
                    </Button>
                  </div>
                </div>
              ) : (
                <button type="button" data-testid="billing-cancel-open" onClick={() => setCancelOpen(true)}
                  className="font-mono text-[10px] uppercase tracking-widest text-slate-600 transition-colors hover:text-slate-300">
                  Cancel subscription
                </button>
              )}
            </div>
          )}

          {(billing.invoices.length > 0 || billing.history.length > 0) && (
            <div className="mt-5 space-y-2" data-testid="billing-history">
              {billing.invoices.map((inv) => (
                <div key={inv.number} className="flex flex-wrap items-center justify-between gap-3 border-b border-ink-700/60 pb-2 text-sm">
                  <span className="text-slate-300">{inv.date} · {inv.number}</span>
                  <span className="flex items-center gap-3">
                    <span className="font-mono text-[10px] uppercase tracking-widest text-slate-500">{inv.status}</span>
                    <span className="text-slate-100">{money(inv.amount)}</span>
                    {inv.pdf_url && (
                      <a href={inv.pdf_url} target="_blank" rel="noreferrer" className="text-gold underline">invoice</a>
                    )}
                  </span>
                </div>
              ))}
              {billing.history.map((h) => (
                <div key={h.session_id} className="flex items-center justify-between gap-3 border-b border-ink-700/60 pb-2 text-sm">
                  <span className="text-slate-300">{h.date} · {h.description}</span>
                  <span className="text-slate-100">{money(h.amount)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {proration && (
        <div data-testid="proration-panel" className="rounded-xl border border-gold/40 bg-gold/5 p-6">
          <p className="font-mono text-[11px] uppercase tracking-widest text-gold">
            Switch to {proration.plan_name}
          </p>
          <p className="mt-2 text-sm text-slate-300">
            You keep credit for the days left on your current plan. Due today:{" "}
            <span data-testid="proration-due" className="text-slate-50">{money(proration.due_now)}</span>
            {proration.credit_applied < 0 && (
              <span className="text-emerald-300"> (includes {money(Math.abs(proration.credit_applied))} credit for unused days)</span>
            )}.
          </p>
          <div className="mt-4 space-y-1" data-testid="proration-lines">
            {proration.lines.map((l, i) => (
              <p key={`${l.description}-${i}`} className="flex justify-between font-mono text-[11px] text-slate-400">
                <span>{l.description}</span>
                <span className={l.amount < 0 ? "text-emerald-300" : "text-slate-200"}>{money(l.amount)}</span>
              </p>
            ))}
          </div>
          <div className="mt-5 flex flex-wrap gap-2">
            <Button data-testid="proration-confirm" disabled={busyKey === "switch"} onClick={confirmSwitch}
              className="bg-gold text-ink-900 hover:bg-gold-light">
              {busyKey === "switch" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirm switch"}
            </Button>
            <Button data-testid="proration-cancel" variant="outline" onClick={() => setProration(null)}
              className="border-ink-600 bg-transparent text-slate-300 hover:text-gold">
              Keep my current plan
            </Button>
          </div>
        </div>
      )}

      {checkoutUrl && (
        <a data-testid="plans-checkout-link" href={checkoutUrl} target="_blank" rel="noreferrer"
          className="block rounded-xl border border-gold/40 bg-gold/10 p-4 text-sm text-gold hover:bg-gold/20">
          Your secure Stripe checkout is ready — tap here to open it in a new tab.
        </a>
      )}

      <div className="flex flex-wrap items-center justify-between gap-4">
        <div data-testid="billing-cycle-picker" className="flex flex-wrap items-center gap-2 rounded-2xl border border-ink-600 bg-ink-800 p-1">
          {terms.map((t) => (
            <button key={t.key} data-testid={`billing-cycle-${t.key}`} onClick={() => setCycle(t.key)}
              className={`rounded-full px-4 py-2 font-mono text-[11px] uppercase tracking-widest transition-colors ${
                cycle === t.key ? "bg-gold text-ink-900" : "text-slate-400 hover:text-slate-100"}`}>
              {t.label}{t.discount_percent ? ` · save ${t.discount_percent}%` : ""}
            </button>
          ))}
        </div>
        {intro?.eligible && (
          <p data-testid="intro-offer" className="rounded-full border border-emerald-500/40 bg-emerald-500/10 px-4 py-2 font-mono text-[11px] uppercase tracking-widest text-emerald-300">
            First {(intro.trial_days || 14) / 7} weeks free — cancel any time before they end
          </p>
        )}
      </div>

      <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
        {plans.map((p) => {
          const current = company?.plan === p.key;
          const term = (p.cycles || []).find((c) => c.key === cycle);
          const lookupKey = term ? term.lookup_key : p.lookup_key;
          return (
            <div
              key={p.key}
              data-testid={`plan-${p.key}`}
              className={`flex flex-col rounded-xl border p-6 ${current ? "border-gold/60 bg-ink-700" : "border-ink-600 bg-ink-800"}`}
            >
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-medium text-slate-100">{p.name}</h2>
                {current && (
                  <span className="rounded-full border border-gold/30 bg-gold/10 px-2.5 py-0.5 font-mono text-[10px] uppercase tracking-widest text-gold">
                    Current
                  </span>
                )}
              </div>
              <p data-testid={`plan-price-${p.key}`} className="mt-3 font-display text-3xl font-bold text-slate-50">
                {p.price === 0 ? "$0" : money(term ? term.price : p.price).replace(".00", "")}
                <span className="ml-1 text-xs font-normal text-slate-500">
                  {p.price === 0 ? "" : term ? term.short : "/week"}
                </span>
              </p>
              {term?.discount_percent > 0 && (
                <p data-testid={`plan-saving-${p.key}`} className="mt-1 font-mono text-[11px] text-emerald-300">
                  {term.discount_percent}% off — saves {money(term.saving).replace(".00", "")}, works out at
                  {" "}{money(term.per_week).replace(".00", "")} a week
                </p>
              )}
              {p.price > 0 && intro?.eligible && (
                <p data-testid={`plan-intro-${p.key}`} className="mt-1 font-mono text-[11px] text-emerald-300">
                  Free for {(intro.trial_days || 14) / 7} weeks, then {money(term ? term.price : p.price).replace(".00", "")}{term ? term.short : "/week"}
                </p>
              )}
              <p className="mt-2 text-xs text-slate-500">{p.tagline}</p>
              <p className="mt-3 font-mono text-[11px] text-gold">
                {p.manifest_limit ? `${p.manifest_limit} trips a week` : "Unlimited trips"}
              </p>
              <ul className="mt-5 flex-1 space-y-2.5">
                {p.features.map((f) => (
                  <li key={f} className="flex gap-2 text-xs leading-relaxed text-slate-400">
                    <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-gold" /> {f}
                  </li>
                ))}
              </ul>
              {lookupKey && billing?.subscribed && !current ? (
                <Button
                  data-testid={`switch-btn-${p.key}`}
                  disabled={busyKey === `preview-${p.key}`}
                  onClick={() => previewSwitch(p, lookupKey)}
                  className="mt-6 w-full bg-gold text-ink-900 hover:bg-gold-light"
                >
                  {busyKey === `preview-${p.key}` ? <Loader2 className="h-4 w-4 animate-spin" />
                    : `Switch to ${p.name}`}
                </Button>
              ) : lookupKey ? (
                <Button
                  data-testid={`upgrade-btn-${p.key}`}
                  disabled={current || busyKey === p.key}
                  onClick={() => upgrade({ ...p, lookup_key: lookupKey })}
                  className={`mt-6 w-full ${current ? "bg-ink-600 text-slate-400" : "bg-gold text-ink-900 hover:bg-gold-light"}`}
                >
                  {busyKey === p.key ? <Loader2 className="h-4 w-4 animate-spin" /> : current ? "Active plan" : `Upgrade to ${p.name}`}
                </Button>
              ) : (
                <p className="mt-6 text-center font-mono text-xs text-slate-500">
                  {current ? "You are on the free tier" : "Free tier"}
                </p>
              )}
            </div>
          );
        })}
      </div>
      <p className="font-mono text-xs text-slate-500">
        Test card 4242 4242 4242 4242 · any future expiry · any CVC.
      </p>
    </div>
  );
}
